// src/auth/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { msalInstance } from "./auth/msal";
import { setTokenProvider } from "./services/api";
import type { AccountInfo, AuthenticationResult } from "@azure/msal-browser";

type AuthContextType = {
  user: AccountInfo | null;
  token: string | null;
  logout: () => void;
};

const AuthCtx = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // pega a conta ativa do MSAL
    const acct = msalInstance.getActiveAccount();
    if (acct) setUser(acct);

    // tenta recuperar token silenciosamente
    const acquire = async () => {
      try {
        const resp: AuthenticationResult = await msalInstance.acquireTokenSilent({
          scopes: [import.meta.env.VITE_API_SCOPE!],
          account: acct ?? undefined,
        });
        setToken(resp.accessToken);
      } catch (err) {
        console.warn("[AuthProvider] acquireTokenSilent falhou:", err);
        setToken(null);
      }
    };

    acquire();
  }, []);

  // injeta token para o api.ts
  useEffect(() => {
    setTokenProvider(async () => token || "");
  }, [token]);

  const logout = () => {
    msalInstance.logoutRedirect();
  };

  const value = useMemo(() => ({ user, token, logout }), [user, token]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  return ctx;
};
